<template>
  <label
    ref="root"
    class="ak-toggle-switch"
    :class="classes"
    :disabled="disabled"
    @click="focus"
    @keydown.prevent.enter="root.click()"
    @mousedown="isMouseDown = true"
    @mouseup="isMouseDown = false"
    @mouseout="isMouseDown = false"
    @blur="isMouseDown = false"
  >
    <input
      ref="input"
      v-model="computedValue"
      type="checkbox"
      :disabled="disabled"
      :name="name"
      :required="required"
      :value="nativeValue"
      :true-value="trueValue"
      :false-value="falseValue"
      @click.stop
    />
    <div class="ak-toggle-switch__handle" />
    <span class="ak-toggle-switch__label nowrap"><slot /></span>
  </label>
</template>

<script>
import {
  defineComponent,
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
} from 'vue'

import { switchProps, validationProps } from '../../mixins/formPropsMixin'

import validationMixin from '../../mixins/validationMixin'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-switch />
   */
  name: 'AkSwitch',

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    ...switchProps,
    ...validationProps,
  },

  emits: ['update:modelValue'],

  /**
   * @param {Object} props - The properties of the component
   * @param {Object} options - The setup options
   * @param {Function} options.emit - The emit method for triggering events
   * @param {Function} options.attrs - The attributes for the component.
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props, { emit, attrs }) {
    const input = ref(null)
    const root = ref(null)
    const isMouseDown = ref(false)

    const computedValue = computed({
      get() {
        return props.modelValue
      },
      set(value) {
        emit('update:modelValue', value)
      },
    })

    /**
     * @return {Object}
     * @description Returns the classes for the switch input
     */
    const classes = computed(() => {
      return {
        'is-disabled': props.disabled,
      }
    })

    /**
     * @description Focues the switch input
     */
    const focus = () => {
      // MacOS FireFox and Safari do not focus when clicked
      input.value.focus()
    }

    /**
     * @type {Object}
     * @description The validation mixin return value, this is an object
     * containing all of the required functionality to handle the
     * validation of the input
     */
    const validation = validationMixin({
      type: 'switch',
      emit,
      props,
      attrs,
      input,
    })

    onMounted(() => validation.validate())
    onBeforeUnmount(() => validation.beforeUnmount())

    watch(computedValue, () => {
      validation.validate()
    })

    watch(
      () => props.modelValue,
      (newVal) => {
        computedValue.value = newVal
      }
    )

    return {
      computedValue,
      input,
      root,
      isMouseDown,
      classes,
      focus,
      ...validation,
    }
  },
})
</script>
<style lang="scss">
@import '../../scss/_settings.scss';
$base-accent-color: $color-primary !default;
$base-background-color: $color-grey-lighter !default;

$switch-width: 3em;
$switch-padding: 0.1em;
$switch-height: 2em;
$switch-radius: $switch-height;
$handle-size: $switch-height - ($switch-padding * 6);
$handle-radius: 50%;
$handle-width: $handle-size;
$switch-background: $base-background-color;
$switch-border-background: $base-border-color;
$switch-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.5);

.ak-toggle-switch {
  border-radius: $switch-radius;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  position: relative;
  margin-bottom: 0;
  vertical-align: middle;

  &.is-disabled {
    &,
    .ak-toggle-switch__handle {
      cursor: not-allowed !important;
    }
  }

  input[type='checkbox'] {
    opacity: 0;
    position: absolute;

    + .ak-toggle-switch__handle {
      align-self: flex-start;
      transition: all 0.1s $easing;
      background: $switch-border-background;
      border-radius: $switch-radius;
      border: none;
      cursor: pointer;
      height: $switch-height;
      margin: 0;
      padding: 0;
      position: relative;
      width: $switch-width;
      z-index: 0;

      &:before {
        position: absolute;
        top: 50%;
        right: 0;
        bottom: 0;
        left: 4px;
        transform: translateY(-50%) scale(1);
        transition: all 0.1s $ease-out;
        border-radius: $switch-radius;
        content: '';
        height: $handle-radius;
        width: $switch-width - ($switch-padding * 2);
        z-index: 1;
      }

      &:after {
        position: absolute;
        top: 50%;
        right: 0;
        bottom: 0;
        left: 4px;
        transform: translateY(-50%);
        transition: all 0.1s $ease-out;
        width: $handle-size;
        height: $handle-size;
        background: #fff;
        border-radius: $handle-radius;
        box-shadow: $switch-shadow;
        content: '';
        z-index: 2;
      }
    }

    &:checked {
      + .ak-toggle-switch__handle {
        background: $base-accent-color;

        &:before {
          transform: scale(0);
        }

        &:after {
          left: $switch-width - $handle-width - ($switch-padding * 2.7);
        }
      }
    }
  }

  &__label {
    margin-left: $spacing-tiny;
  }
}
</style>
